import { useCallback, useMemo } from "react";
import { useWizardContext } from "../AppointmentWizardContext";
import { logout } from "../../../auth";
import * as api from "../../../services/api";
import { status } from "shared/src/appointment.mjs";
import { useSnackbarMessage } from "../../Snackbar.jsx";
import { useTranslation } from "react-i18next";
import { role } from "shared/src/roles.mjs";
import { useNavigate } from "../../../url";

export const WIZARD_STEPS = {
  PREPARATION: 0,
  COMPLETION: 1,
  RECEPTION_NOTES: 2,
  PROCEDURE_NOTES: 3,
  APPOINTMENT_FINISHED: 4,
  CHAIR_CLEANING: 5,
};

const STEPS_CONFIG = {
  [WIZARD_STEPS.PREPARATION]: {
    label: "Preparation",
    nextLabel: "Příprava hotová",
    canProgress: true,
  },
  [WIZARD_STEPS.COMPLETION]: {
    label: "Completion",
    nextLabel: "Dokončit úkon",
    canProgress: true,
  },
  [WIZARD_STEPS.RECEPTION_NOTES]: {
    label: "Reception Notes",
    nextLabel: "Odeslat na Recepci",
    canProgress: true,
  },
  [WIZARD_STEPS.PROCEDURE_NOTES]: {
    label: "Procedure Notes",
    nextLabel: "Zápis dokončen",
    canProgress: true,
  },
};

export const useWizardSteps = () => {
  const { t } = useTranslation();
  const displaySnackbarMessage = useSnackbarMessage();
  const {
    activeStep,
    setActiveStep,
    currentAppointmentId,
    nextAppointmentNote,
    clientData,
    receptionistNotes,
    floorManagerNotes,
    setIsCompleting,
    appointmentNeeded,
    rtgCreated,
    rtgNotRequired,
    rtgBypassReason,
  } = useWizardContext();
  const navigate = useNavigate();

  const currentAppointment = useMemo(() => {
    return clientData?.todaysAppointments?.find(
      (appointment) => appointment.id === currentAppointmentId,
    );
  }, [clientData, currentAppointmentId]);

  const serviceId = currentAppointment?.service?.id || "";
  const serviceName = currentAppointment?.service?.name || "";
  const serviceType = currentAppointment?.service?.type || "";

  const requiresRtgForThisService = useMemo(() => {
    const ls = currentAppointment?.service?.locationService;
    const requires = Boolean(ls?.requires_rentgen);
    const types = Array.isArray(ls?.actual_calendar?.types)
      ? ls.actual_calendar.types
      : [];
    return requires && types.includes("other");
  }, [currentAppointment]);

  const isBlank = (v) => !v || v.trim().length === 0;

  const adhocType = useMemo(() => {
    const audit = currentAppointment?.audit?.data ?? [];

    const insertRow = audit.find((r) => r?.action === "I");
    const valFromInsert = insertRow?.row_data?.adhoc_type ?? null;
    if (typeof valFromInsert === "string" && valFromInsert.length) {
      return valFromInsert;
    }

    const anyWithAdhoc = audit.find((r) => r?.row_data?.adhoc_type);
    const fallback = anyWithAdhoc?.row_data?.adhoc_type ?? null;
    return typeof fallback === "string" && fallback.length ? fallback : null;
  }, [currentAppointment]);

  const isAdhocChair = adhocType === "CHAIR";

  const skipReceptionByServiceId = useMemo(
    () =>
      new Set([
        "67746ddf-111e-4714-a856-b15d7b4f591b", // Sken před konzultací – Praha
        "b2ba9fc0-71cf-481c-89de-b087763cd4e0", // Sken před konzultací – Brno
      ]),
    [],
  );

  const skipReceptionNotes = useMemo(() => {
    if (serviceType === "rentgen" && isAdhocChair) return true;
    if (skipReceptionByServiceId.has(serviceId)) return true;
    return false;
  }, [serviceType, isAdhocChair, skipReceptionByServiceId, serviceId]);

  const shouldSkipStep = useCallback(
    (step) => {
      if (skipReceptionNotes) {
        return step === WIZARD_STEPS.RECEPTION_NOTES;
      }
      return false;
    },
    [skipReceptionNotes],
  );

  const getNextValidStep = useCallback(
    (currentStep) => {
      let nextStep = currentStep + 1;
      while (
        nextStep <= WIZARD_STEPS.CHAIR_CLEANING &&
        shouldSkipStep(nextStep)
      ) {
        nextStep++;
      }

      if (nextStep > WIZARD_STEPS.CHAIR_CLEANING) {
        return WIZARD_STEPS.APPOINTMENT_FINISHED;
      }

      return nextStep;
    },
    [shouldSkipStep],
  );

  const getStepConfig = useCallback(
    (step) => STEPS_CONFIG[step] || STEPS_CONFIG[WIZARD_STEPS.PREPARATION],
    [],
  );

  const handleProgressStep = useCallback(async () => {
    const currentConfig = getStepConfig(activeStep);
    if (!currentConfig.canProgress) return false;

    if (!shouldSkipStep(activeStep)) {
      switch (activeStep) {
        case WIZARD_STEPS.PREPARATION:
          {
            try {
              await api.patchAppointment({
                id: currentAppointmentId,
                status_id: status.TREATING,
              });
            } catch (err) {
              console.error("Failed to update appointment status:", err);
            }
          }
          break;
        case WIZARD_STEPS.PROCEDURE_NOTES:
          {
            try {
              if (floorManagerNotes && currentAppointmentId) {
                await api.createAppointmentNote({
                  appointment_id: currentAppointmentId,
                  note_type: "floor_manager",
                  note_content: floorManagerNotes,
                });
              }
            } catch (err) {
              console.error("Failed to save floor manager notes:", err);
            }
          }
          break;
        case WIZARD_STEPS.COMPLETION:
          {
            try {
              await api.patchAppointment({
                id: currentAppointmentId,
                status_id: status.TREATED,
              });
            } catch (err) {
              console.error(
                "Failed to update appointment status to TREATED:",
                err,
              );
            }
          }
          break;
      }
    }

    const nextStep = getNextValidStep(activeStep);
    await setActiveStep(nextStep);

    if (nextStep === WIZARD_STEPS.APPOINTMENT_FINISHED) {
      setIsCompleting(true);

      try {
        await api.patchAppointment({
          id: currentAppointmentId,
          active_step: "APPOINTMENT_FINISHED",
        });

        if (clientData?.id != null && !isBlank(nextAppointmentNote)) {
          await api.createNextAppointmentNote({
            input: {
              client_id: clientData.id,
              note: String(nextAppointmentNote ?? ""),
              role: role.Dental_Assistant,
            },
          });
        }
      } catch (err) {
        console.error("Failed to save next appointment note:", err);
      }

      localStorage.setItem("lastAppointmentId", currentAppointmentId);
      await logout({ navigate });
    }
    return true;
  }, [
    activeStep,
    getStepConfig,
    setActiveStep,
    currentAppointmentId,
    nextAppointmentNote,
    clientData,
    receptionistNotes,
    floorManagerNotes,
    setIsCompleting,
    t,
    shouldSkipStep,
    getNextValidStep,
    displaySnackbarMessage,
    appointmentNeeded,
    requiresRtgForThisService,
    rtgCreated,
    rtgNotRequired,
    rtgBypassReason,
  ]);

  const setSpecificStep = useCallback(
    async (step) => {
      if (step >= 0 && step <= WIZARD_STEPS.CHAIR_CLEANING) {
        await setActiveStep(step);
      }
    },
    [setActiveStep],
  );

  const isStepCompleted = useCallback(
    (step) => step < activeStep,
    [activeStep],
  );

  const isStepActive = useCallback((step) => step === activeStep, [activeStep]);

  const isStepSkipped = useCallback(
    (step) => shouldSkipStep(step),
    [shouldSkipStep],
  );

  const getStepButtonLabel = useCallback(
    () => getStepConfig(activeStep).nextLabel,
    [activeStep, getStepConfig],
  );

  return {
    activeStep,
    handleProgressStep,
    setSpecificStep,
    isStepCompleted,
    isStepActive,
    isStepSkipped,
    getStepButtonLabel,
    getStepConfig,
    serviceId,
    serviceType,
    serviceName,
    skipReceptionNotes,
    getNextValidStep,
  };
};
